import { downloadJson } from '../downloadJson'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus } from 'lucide-react'
import { useState } from 'react'
import type { ExamsApi, Sitting, SittingDates } from '../api/exams'
import { SittingDrawer } from './SittingDrawer'
import { SittingsGrid } from './SittingsGrid'

/**
 * Exam > Sittings: every dated window in which the Exam is sat.
 *
 * Every future Sitting is entered the day it is published, not one cycle at a time
 * (app:R67): the reduction window, the Dashboard countdown and an Entitlement's expiry all
 * resolve against these dates.
 *
 * A Sitting is never removed. Its name is what an Entitlement holds (app:R42), so the
 * screen offers add and change and nothing else.
 */
export function Sittings({ exams, exam }: { exams: ExamsApi; exam: string }) {
  const client = useQueryClient()
  const catalogue = useQuery({ queryKey: ['exams'], queryFn: () => exams.list() })

  const [opened, setOpened] = useState<Sitting | null>(null)
  const [adding, setAdding] = useState(false)

  const found = catalogue.data?.find((entry) => entry.code === exam) ?? null
  const rows = found?.sittings ?? []

  const save = useMutation({
    mutationFn: (change: { name: string; dates: SittingDates }) =>
      adding
        ? exams.addSitting(exam, change.name, change.dates)
        : exams.changeSitting(exam, change.name, change.dates),
    onSuccess: () => {
      setOpened(null)
      setAdding(false)
      void client.invalidateQueries({ queryKey: ['exams'] })
    },
  })

  const statistics = useMutation({
    mutationFn: async () => downloadJson(`${exam.toLowerCase()}-statistics.json`, await exams.exportStatistics(exam)),
  })

  const close = () => {
    setOpened(null)
    setAdding(false)
    save.reset()
  }

  const failure = catalogue.error?.message ?? null

  return (
    <>
      <div className="flex flex-wrap items-center gap-4">
        <h2 className="font-heading text-h2 font-bold text-heading">Sittings</h2>
        <button
          type="button"
          className={`${quiet} ml-auto`}
          onClick={() => {
            setAdding(true)
            setOpened({ name: '', bookingClosesOn: '', testsFrom: '', testsTo: '' })
          }}
        >
          <Plus aria-hidden size={16} /> Add a Sitting
        </button>
      </div>
      {failure && (
        <p role="alert" className="mt-4 border-l-2 border-red-700 pl-3 text-red-700">
          {failure}
        </p>
      )}

      <button type="button" className={`${quiet} my-4`} disabled={statistics.isPending}
        onClick={() => statistics.mutate()}>Export {exam} statistics</button>
      {statistics.error && <p role="alert">{statistics.error.message}</p>}
      <SittingsGrid
        rows={rows}
        pending={catalogue.isPending}
        onOpen={(name) => {
          setAdding(false)
          setOpened(rows.find((sitting) => sitting.name === name) ?? null)
        }}
      />

      {opened && (
        <SittingDrawer
          sitting={opened}
          adding={adding}
          saving={save.isPending}
          error={save.error?.message ?? null}
          onSave={(name, dates) => save.mutate({ name, dates })}
          onClose={close}
        />
      )}
    </>
  )
}

const quiet =
  'inline-flex items-center gap-2 rounded-md border border-green-800 px-4 py-2 font-bold text-green-800 hover:bg-green-050 disabled:opacity-55'
